<script lang="ts" setup>
import type { TripOrder, TripRouteStop, TripStop } from "@/types/trip";
import RouteStopIcon from "@/components/icons/RouteStopIcon.vue";
import FacilityInfo from "@/views/trips/components/FacilityInfo.vue";
import { useFreights, useOrder, useTrip } from "@/composables";
import type { OrderFreightDto } from "@/api/orders/dto/order.dto";
import { computed, ref, watch } from "vue";
import { RouteStopStatus } from "@/constants/trip";
import type { TravelOrderDto } from "@/api/trips/dto/trip.dto";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import StopDateRange from "@/views/orders/components/StopDateRange.vue";
import DeletedChip from "@/components/chips/DeletedChip.vue";

const { orderColorList } = useOrder();
const { isTravelOrderCancelled } = useTrip();
const { timeTypesMap, getStopTime } = useOrder();
const { getFreightName } = useFreights();
const { t } = useI18n();

const props = defineProps<{
  tripStop: TripStop;
  tripStops: TripStop[];
  routeStops: TripRouteStop[];
  orderFreights: OrderFreightDto[];
  travelOrders: TravelOrderDto[];
  orders: TripOrder[];
}>();

const emits = defineEmits<{
  (e: "move", stop: TripStop, direction: number): void;
  (e: "save", stop: TripStop): void;
}>();

const isEdit = ref<boolean>(false);
const editableTripStop = ref<TripStop | null>(null);

const blockUpOptions = computed(() => {
  return checkMoveBlocked(-1);
});

const blockDownOptions = computed(() => {
  return checkMoveBlocked(1);
});

const orderColors = computed(() => {
  return props.orders.reduce<Record<string, string>>((acc, order, index) => {
    if (order.id) {
      acc[order.id] = orderColorList[index];
    }

    return acc;
  }, {});
});

const borderColor = computed(() => {
  if (props.tripStop.isReload) {
    return "reload";
  }

  if (props.tripStop.isDeleted) {
    return "deleted";
  }

  return props.tripStop.order ? orderColors.value[props.tripStop.order.id || ""] : "";
});

const tripStopFreights = computed(() => {
  if (props.tripStop.isReload) {
    return props.tripStop.reloadFreights || [];
  }

  const freights = [];

  for (const freight of props.orderFreights) {
    if (freight.id && props.tripStop.freights.includes(freight.id)) {
      freights.push({ ...freight, name: getFreightName(freight, freight.number, "", false) });
    }
  }
  return freights;
});

const dateError = computed<string>(() => {
  if (!editableTripStop.value || !editableTripStop.value.timeTo) {
    return "";
  }

  const currentIndex = props.tripStops.findIndex((tripStop) => tripStop.id === editableTripStop.value?.id);
  if (currentIndex === 0 || currentIndex === -1) {
    return "";
  }

  const prevTimeTo = dayjs(props.tripStops[currentIndex - 1].timeTo);
  const currentTimeTo = dayjs(editableTripStop.value.timeTo);

  const hasInvalidDate = currentTimeTo.isBefore(prevTimeTo);

  return hasInvalidDate ? t("trip.time-is-earlier-stop") : "";
});

function checkMoveBlocked(direction: number) {
  const result: {
    blocked: boolean;
    message: null | string;
  } = {
    blocked: false,
    message: null,
  };

  if (!props.tripStop) {
    return result;
  }

  const stopIndex = props.tripStops.findIndex((s) => s.id === props.tripStop.id);
  const targetIndex = stopIndex + direction;
  const targetStop = props.tripStops[targetIndex];

  if (!targetStop) {
    result.blocked = true;
    return result;
  }

  if (props.tripStop.isReload || targetStop.isReload) {
    result.blocked = true;
    result.message = "Reload stop position can’t be changed";
    return result;
  }

  if (props.tripStop.isDeleted || targetStop.isDeleted) {
    result.blocked = true;
    result.message = "Deleted stop position can`t be changed";
    return result;
  }

  const isSameOrder = targetStop.order?.id === props.tripStop.order?.id;
  const isSameType = targetStop.type === props.tripStop.type;
  const hasSameFreight = props.tripStop.freights.some((freight) => targetStop.freights.includes(freight));

  if (isSameOrder && hasSameFreight && !isSameType) {
    result.blocked = true;
    result.message = "Pickup and Delivery can’t be reordered due to the same order freight.";
  }

  const relatedRouteStops = props.routeStops.filter((routeStop) => {
    return routeStop.tripStopId === props.tripStop.id || routeStop.tripStopId === targetStop?.id;
  });

  const availableRouteStopStatuses = [RouteStopStatus.notStarted, RouteStopStatus.inRoute];
  const hasRouteStopInWrongStatus = !relatedRouteStops.every((routeStop) =>
    availableRouteStopStatuses.includes(routeStop.status),
  );

  if (hasRouteStopInWrongStatus && relatedRouteStops.length) {
    result.blocked = true;
    result.message = "Can’t be reordered after progress has started";
  }

  const travelOrderTempIds = relatedRouteStops.flatMap((routeStop) => routeStop.travelOrderTempIds);

  const relatedTravelOrders = props.travelOrders.filter((to) => {
    return travelOrderTempIds.includes(to.tempId);
  });

  const hasTravelOrderInCancelStatus = relatedTravelOrders.some((to) => isTravelOrderCancelled(to));

  if (hasTravelOrderInCancelStatus) {
    result.blocked = true;
    result.message = "Can’t be reordered due to canceled or dry run travel orders.";
  }

  return result;
}

function moveUp() {
  if (!props.tripStop) {
    return;
  }

  emits("move", props.tripStop, -1);
}

function moveDown() {
  if (!props.tripStop) {
    return;
  }

  emits("move", props.tripStop, 1);
}

function onSave() {
  if (editableTripStop.value) {
    emits("save", editableTripStop.value);
  }

  isEdit.value = !isEdit.value;
}

watch(
  dateError,
  (value) => {
    if (value) {
      isEdit.value = true;
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => props.tripStop,
  (value) => {
    editableTripStop.value = {
      ...value,
    };
  },
  {
    immediate: true,
    deep: true,
  },
);
</script>

<template>
  <div :class="borderColor" class="trip-stop__block">
    <div class="trip-stop__block-inner pa-4 bg-grey-lighten-4 rounded">
      <div class="block-header d-flex align-center justify-space-between mb-4">
        <div class="block-header__left d-flex align-center flex-wrap font-size-12 w-100 mr-2">
          <route-stop-icon
            :is-deleted="tripStop.isDeleted"
            :is-reload="tripStop.isReload"
            :type="tripStop.type"
            class="mr-2"
            size="16"
          />
          <span :class="tripStop.isDeleted ? 'text-grey' : 'text-blue-grey-darken-1'" class="font-weight-600 mr-3">{{
            tripStop.name
          }}</span>
          <router-link
            v-if="!tripStop.isDeleted && tripStop.order"
            :to="{ name: 'order-view', params: { id: tripStop.order?.id } }"
            class="d-block text-primary font-weight-500 font-size-12 text-decoration-none"
            data-qa="route-trip-stop-number"
            target="_blank"
          >
            Order #{{ tripStop.order?.number }}
          </router-link>
          <span v-if="tripStop.isDeleted" class="text-grey font-weight-500 font-size-12">
            Order #{{ tripStop.order?.number }}
          </span>

          <template v-if="!tripStop.isDeleted">
            <v-btn
              v-if="!isEdit"
              class="ml-auto"
              color="primary"
              icon="mdi-pencil-outline"
              size="small"
              variant="text"
              @click="isEdit = !isEdit"
            />

            <v-btn
              v-else
              :disabled="Boolean(dateError)"
              class="ml-auto"
              color="primary"
              flat
              size="small"
              @click="onSave"
            >
              {{ t("general.save") }}
            </v-btn>
          </template>
        </div>

        <div class="block-header__right d-flex flex-column">
          <template v-if="!tripStop.isDeleted">
            <v-tooltip :disabled="!blockUpOptions.message" location="top">
              <template #activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps">
                  <v-btn
                    :disabled="blockUpOptions.blocked"
                    icon="mdi-chevron-up"
                    size="xs"
                    variant="plain"
                    @click="moveUp"
                  ></v-btn>
                </div>
              </template>
              {{ blockUpOptions.message }}
            </v-tooltip>

            <v-tooltip :disabled="!blockDownOptions.message" location="top">
              <template #activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps">
                  <v-btn
                    :disabled="blockDownOptions.blocked"
                    icon="mdi-chevron-down"
                    size="xs"
                    variant="plain"
                    @click="moveDown"
                  ></v-btn>
                </div>
              </template>
              {{ blockDownOptions.message }}
            </v-tooltip>
          </template>

          <deleted-chip v-else size="default"></deleted-chip>
        </div>
      </div>

      <facility-info
        :disabled="tripStop.isDeleted"
        :facility="tripStop.facility"
        :updated="false"
        class="mb-5"
      ></facility-info>

      <div v-if="isEdit">
        <stop-date-range
          v-if="editableTripStop"
          v-model="editableTripStop"
          :hide2-time-frame="tripStop.isReload"
          :multiline="!tripStop.isReload"
        >
          <template v-if="dateError" #error>
            <span class="font-size-12 text-danger">{{ dateError }}</span>
          </template>
        </stop-date-range>
      </div>

      <div v-else>
        <div class="d-flex align-center">
          <v-icon class="mr-2 text-grey-darken-1" size="17">mdi-clock-outline</v-icon>
          <span v-if="tripStop.timeType" class="body-2 text-grey-darken-1 font-weight-medium">
            {{ timeTypesMap[tripStop.timeType] }}: {{ getStopTime(tripStop) }}
          </span>
        </div>
      </div>

      <div class="d-flex mt-5">
        <div class="text-grey-darken-1 mr-2">Freights:</div>
        <div
          :class="tripStop.isDeleted ? 'text-grey-darken-1' : 'text-grey-darken-2'"
          class="font-weight-500"
          data-qa="freight-size"
        >
          <div v-for="freight in tripStopFreights" :key="freight.id">
            {{ freight.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.trip-stop__block {
  &:deep(.v-text-field) {
    background-color: $white;
  }
}
</style>
